'use client'

import * as React from 'react'
import { StatusPanel, type StatusAction } from '@/components/status/status-panel'
import { richText } from '@/lib/i18n/rich-text'
import { useShellTranslation } from '@/lib/shell/use-shell-translation'
import { useCaptureCaughtError } from '@/lib/sentry/capture-caught-error'

/**
 * What every error boundary shows: the product's 500 page. "Try again" runs
 * the boundary's `reset`; the digest Next attaches to the error is shown as a
 * support reference (left to right, easy to select); what was thrown never
 * is — it is English written for a developer and belongs in the console and
 * Sentry. The panel reports it to Sentry itself, once and with that digest
 * (lib/sentry/capture-caught-error.ts), so no boundary that shows it can
 * forget to.
 *
 * `page` fills the window (app/error.tsx, which stands in for every layout);
 * the module boundaries sit beside their sidebar (`section`). A boundary that
 * already had its own words keeps them (`title`, `body`, `retryLabel`).
 */
export function RouteErrorPanel({
  error,
  reset,
  variant = 'section',
  title,
  body,
  retryLabel,
  secondary,
}: {
  error: Error & { digest?: string }
  reset: () => void
  variant?: 'page' | 'section'
  title?: React.ReactNode
  body?: React.ReactNode
  retryLabel?: string
  secondary?: StatusAction | null
}) {
  const { t } = useShellTranslation()
  useCaptureCaughtError(error)
  return (
    <StatusPanel
      kind="500"
      variant={variant}
      title={title}
      body={body}
      onRetry={reset}
      primary={retryLabel ? { label: retryLabel, onClick: reset, icon: 'retry' } : undefined}
      secondary={secondary}
      footer={
        error.digest ? (
          <span data-testid="status-reference">
            {richText(t('shell.status.reference'), {
              digest: (
                <code dir="ltr" className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-foreground select-all">
                  {error.digest}
                </code>
              ),
            })}
          </span>
        ) : null
      }
    />
  )
}
